import { CheckCircle2, ClipboardList, Clock, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import useLocale from '../contexts/LocaleContext';
import { useSurveys } from '../hooks/useSurveys';
import type { SurveyDto } from '../types/api';
import { Card, CardContent } from '../components/ui/Card';

const localizedTitle = (survey: SurveyDto, locale: string) =>
  locale === 'rw'
    ? survey.titleKinyarwanda || survey.title
    : locale === 'fr'
      ? survey.titleFrench || survey.title
      : survey.title;

export default function Surveys() {
  const { locale, dictionary } = useLocale();
  const { data: surveys, isLoading } = useSurveys();

  return (
    <div className='p-4 sm:p-6 space-y-4 max-w-[1600px] mx-auto'>
      <div>
        <h1 className='text-xl font-semibold text-gray-900'>{dictionary['surveys.title'] || 'Surveys'}</h1>
        <p className='text-sm text-gray-500 mt-0.5'>
          {dictionary['surveys.subtitle'] || 'Data collection surveys for your jurisdiction'}
        </p>
      </div>

      {isLoading ? (
        <div className='flex items-center gap-2 py-10 justify-center text-sm text-gray-500'>
          <Loader2 className='h-4 w-4 animate-spin' />
          {dictionary['common.loading'] || 'Loading...'}
        </div>
      ) : !surveys || surveys.length === 0 ? (
        <Card>
          <CardContent className='py-10 text-center text-sm text-gray-500'>
            <ClipboardList className='h-8 w-8 mx-auto mb-2 text-gray-300' />
            {dictionary['surveys.empty'] || 'No surveys concern your area at the moment.'}
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
          {surveys.map(survey => {
            const expected = survey.expectedCount ?? 0;
            const responded = survey.respondedCount ?? 0;
            const percent = expected > 0 ? Math.round((responded / expected) * 100) : 0;
            const complete = expected > 0 && responded >= expected;
            return (
              <Link key={survey.id} to={`/surveys/${survey.id}`}>
                <Card className='hover:border-primary/50 transition-colors h-full'>
                  <CardContent className='p-4 space-y-3'>
                    <div className='flex items-start justify-between gap-2'>
                      <h3 className='text-sm font-semibold text-gray-900'>{localizedTitle(survey, locale)}</h3>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${
                          survey.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700'
                            : survey.status === 'CLOSED'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {survey.status === 'ACTIVE'
                          ? dictionary['surveys.status_active'] || 'Active'
                          : survey.status === 'CLOSED'
                            ? dictionary['surveys.status_closed'] || 'Closed'
                            : dictionary['surveys.status_draft'] || 'Draft'}
                      </span>
                    </div>
                    {survey.description && <p className='text-xs text-gray-500 line-clamp-2'>{survey.description}</p>}
                    <div className='text-xs text-gray-500 flex items-center gap-1'>
                      <Clock className='h-3.5 w-3.5' />
                      {dictionary['surveys.answered_at_level'] || 'Answered at'}{' '}
                      <span className='font-medium text-gray-700'>{survey.respondentLocationType}</span>
                      {survey.dueDate && <span className='ml-1'>· {new Date(survey.dueDate).toLocaleDateString()}</span>}
                    </div>
                    <div>
                      <div className='flex justify-between text-xs mb-1'>
                        <span className='text-gray-500'>
                          {responded}/{expected} {dictionary['surveys.responded'] || 'responded'}
                        </span>
                        <span className={`font-semibold ${complete ? 'text-green-600' : 'text-gray-700'}`}>
                          {complete && <CheckCircle2 className='inline h-3.5 w-3.5 mr-0.5' />}
                          {percent}%
                        </span>
                      </div>
                      <div className='w-full bg-gray-100 rounded-full h-1.5'>
                        <div
                          className={`h-1.5 rounded-full transition-all ${complete ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
